import React, { useState, useCallback } from "react";
import { Modal, Form, Input, DatePicker, Button, Table, Tag, Space, Alert, Typography, message } from "antd";
import dayjs from "dayjs";
import { mawbStockService } from "../../../../api/enhancedApi";

const { TextArea } = Input;
const { Text } = Typography;

// 单行状态 → 展示标签（与后端 mawb_import.ROW_* 对应）
const STATUS_META = {
    OK: { color: "green", label: "可导入" },
    INVALID_LENGTH: { color: "red", label: "位数不对" },
    BAD_CHECK_DIGIT: { color: "orange", label: "校验位非法" },
    DUP_IN_BATCH: { color: "gold", label: "批内重复" },
    DUP_IN_DB: { color: "default", label: "系统已存在" },
};

const PLACEHOLDER = [
    "粘贴或输入主单号，支持换行 / 逗号 / 空格 / 分号分隔，单号内可带 - 等符号：",
    "317-1031-6261",
    "317-1031-6279",
    "160-1234-5675",
].join("\n");

/**
 * 离散主单号导入弹窗（两步式）
 *  1) 预览解析：dry_run=true，逐行回显状态与汇总，不落库
 *  2) 确认导入：dry_run=false，仅写入「可导入」的单号
 * 修改文本后会清空预览，必须重新预览才能导入，避免导入与预览不一致的集合。
 */
const MawbImportModal = ({ open, onCancel, onSuccess }) => {
    const [form] = Form.useForm();
    const [preview, setPreview] = useState(null);
    const [previewing, setPreviewing] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const resetAll = useCallback(() => {
        form.resetFields();
        setPreview(null);
        setPreviewing(false);
        setSubmitting(false);
    }, [form]);

    const handleCancel = () => {
        resetAll();
        onCancel();
    };

    // 文本变化使已有预览失效，强制重新预览后才能导入
    const handleTextChange = () => {
        if (preview) setPreview(null);
    };

    const buildPayload = async (dryRun) => {
        const values = await form.validateFields();
        return {
            raw_text: values.raw_text,
            received_date: values.received_date.format("YYYY-MM-DD"),
            dry_run: dryRun,
        };
    };

    const handlePreview = async () => {
        let payload;
        try {
            payload = await buildPayload(true);
        } catch {
            return; // 校验未通过
        }
        setPreviewing(true);
        try {
            const res = await mawbStockService.importNumbers(payload);
            setPreview(res?.data?.data || null);
        } catch (err) {
            message.error(err?.response?.data?.detail || "解析失败，请重试");
        } finally {
            setPreviewing(false);
        }
    };

    const handleImport = async () => {
        if (!preview || preview.summary.ok <= 0) {
            message.warning("没有可导入的主单号");
            return;
        }
        let payload;
        try {
            payload = await buildPayload(false);
        } catch {
            return;
        }
        setSubmitting(true);
        try {
            const res = await mawbStockService.importNumbers(payload);
            message.success(res?.data?.message || "导入成功");
            resetAll();
            onSuccess();
        } catch (err) {
            message.error(err?.response?.data?.detail || "导入失败，请重试");
        } finally {
            setSubmitting(false);
        }
    };

    const summary = preview?.summary;
    const canImport = !!preview && summary?.ok > 0;

    const columns = [
        { title: "序号", key: "idx", width: 56, align: "center", render: (_, __, i) => i + 1 },
        { title: "原文", dataIndex: "raw", key: "raw", width: 160, ellipsis: true },
        {
            title: "识别结果", key: "parsed", width: 150,
            render: (_, r) => (r.full_mawb ? r.full_mawb : <Text type="secondary">—</Text>),
        },
        {
            title: "状态", dataIndex: "status", key: "status", width: 110, align: "center",
            render: (s) => {
                const meta = STATUS_META[s] || { color: "default", label: s };
                return <Tag color={meta.color}>{meta.label}</Tag>;
            },
        },
        { title: "说明", dataIndex: "reason", key: "reason", ellipsis: true },
    ];

    return (
        <Modal
            title="导入离散主单号"
            open={open}
            onCancel={handleCancel}
            width={760}
            destroyOnClose
            footer={[
                <Button key="cancel" onClick={handleCancel}>
                    取消
                </Button>,
                <Button key="preview" onClick={handlePreview} loading={previewing}>
                    预览解析
                </Button>,
                <Button
                    key="import"
                    type="primary"
                    disabled={!canImport}
                    loading={submitting}
                    onClick={handleImport}
                >
                    确认导入{canImport ? ` (${summary.ok})` : ""}
                </Button>,
            ]}
        >
            <Alert
                style={{ marginBottom: 12 }}
                type="info"
                showIcon
                message="自动去掉符号后按 3 位航司码 + 7 位序列号 + 1 位校验位识别；位数不对、校验位非法、重复的单号会被跳过。"
            />
            <Form form={form} layout="vertical" preserve={false}>
                <Form.Item
                    name="raw_text"
                    label="主单号文本"
                    rules={[{ required: true, message: "请输入要导入的主单号" }]}
                >
                    <TextArea rows={7} placeholder={PLACEHOLDER} onChange={handleTextChange} allowClear />
                </Form.Item>
                <Form.Item
                    name="received_date"
                    label="收到日期"
                    initialValue={dayjs()}
                    rules={[{ required: true, message: "请选择收到日期" }]}
                >
                    <DatePicker style={{ width: 220 }} />
                </Form.Item>
            </Form>

            {summary && (
                <>
                    <Space wrap style={{ marginBottom: 8 }}>
                        <Text>共解析 {summary.total}：</Text>
                        <Tag color="green">可导入 {summary.ok}</Tag>
                        {summary.invalid_length > 0 && <Tag color="red">位数不对 {summary.invalid_length}</Tag>}
                        {summary.bad_check_digit > 0 && <Tag color="orange">校验位非法 {summary.bad_check_digit}</Tag>}
                        {summary.dup_in_batch > 0 && <Tag color="gold">批内重复 {summary.dup_in_batch}</Tag>}
                        {summary.dup_in_db > 0 && <Tag>系统已存在 {summary.dup_in_db}</Tag>}
                    </Space>
                    <Table
                        size="small"
                        rowKey={(_, i) => i}
                        columns={columns}
                        dataSource={preview.rows}
                        pagination={{ pageSize: 10, size: "small", showSizeChanger: false }}
                        scroll={{ y: 280 }}
                    />
                </>
            )}
        </Modal>
    );
};

export default MawbImportModal;
